import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { FeatherRegularV6 } from "@shared/v2/Icomoon";
import TextLink from "@shared/v2/TextLink";
import { HandwrittenCardModal, useHandwrittenCardPreview } from "@shared/v2/HandwrittenCard";
import { getInkedCampaignPreviewLinkApi } from "../../../PersonDetail/shared/api";
import BaseEvent from "./BaseEvent";
import { noNamer } from "../../../Contacts/ModalViews/helpers";
import formatRecipientAddress from "./helpers/recipientAddress";
import useCampaignThumbnail from "./helpers/useCampaignThumbnail";

// Shared layout for the Inked handwritten card timeline items. Each event type wraps it and supplies its own wording.
const TimelineHandwrittenCampaignCard = ({
  action,
  actor,
  interactedAt,
  campaignId,
  campaignName,
  deliveryId,
  recipientAddress,
  displayDate,
  existsOnInked,
  showPreviewLink,
}) => {
  const person = useSelector((state) => state.personDetail.person);
  const leadName = noNamer(person?.data?.attributes?.first_name, person?.data?.attributes?.last_name);

  const thumbnailUrl = useCampaignThumbnail(existsOnInked ? campaignId : null);
  const { title: previewTitle, isLoading, signedUrl, error, isPreviewOpen, openPreview, closePreview } =
    useHandwrittenCardPreview();

  const addressLine = formatRecipientAddress(recipientAddress);

  const title = (
    <div>
      <span className="tw-font-semibold">{actor}</span>
      {` ${action} a handwritten card for `}
      <span className="tw-font-semibold">{leadName}</span>
    </div>
  );

  // Opens Inked's campaign view on this card, with the campaign's other cards listed beside it.
  const handlePreviewCard = (clickEvent) => {
    clickEvent.preventDefault();
    openPreview("Preview Handwritten Card", () => getInkedCampaignPreviewLinkApi(campaignId, deliveryId));
  };

  return (
    <BaseEvent icon={<FeatherRegularV6 />} title={title} interactedAt={interactedAt}>
      <div className="tw-flex tw-flex-row tw-gap-[12px] tw-mt-[8px]">
        {thumbnailUrl && (
          <img
            src={thumbnailUrl}
            alt="Card preview"
            className="tw-max-w-[120px] tw-rounded-[4px] tw-border tw-border-solid tw-border-gray-15"
          />
        )}

        <div className="tw-text-gray-75 tw-flex tw-flex-col tw-gap-[4px]">
          {campaignName && <div>Campaign: {campaignName}</div>}

          {addressLine && <div>Address: {addressLine}</div>}

          {displayDate && <div>Send Date: {displayDate}</div>}

          {campaignId && existsOnInked && showPreviewLink && (
            <div className="tw-mt-[4px]">
              <TextLink onClick={handlePreviewCard} size="medium">
                Preview Card
              </TextLink>
            </div>
          )}
        </div>
      </div>

      <HandwrittenCardModal
        show={isPreviewOpen}
        onHide={closePreview}
        isLoading={isLoading}
        iframeUrl={signedUrl}
        error={error}
        title={previewTitle}
      />
    </BaseEvent>
  );
};

TimelineHandwrittenCampaignCard.propTypes = {
  action: PropTypes.string.isRequired,
  actor: PropTypes.string.isRequired,
  interactedAt: PropTypes.string,
  campaignId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  campaignName: PropTypes.string,
  deliveryId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  recipientAddress: PropTypes.shape({
    address1: PropTypes.string,
    address2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    postcode: PropTypes.string,
    country: PropTypes.string,
  }),
  displayDate: PropTypes.string,
  // False once the campaign has been deleted on Inked's side: no thumbnail and no preview to open.
  existsOnInked: PropTypes.bool,
  showPreviewLink: PropTypes.bool,
};

TimelineHandwrittenCampaignCard.defaultProps = {
  interactedAt: "",
  campaignId: undefined,
  campaignName: undefined,
  deliveryId: undefined,
  recipientAddress: undefined,
  displayDate: null,
  existsOnInked: true,
  showPreviewLink: true,
};

export default TimelineHandwrittenCampaignCard;
